import * as React from 'react';
import {
    useReactTable,
    getCoreRowModel,
    ColumnFiltersState,
    SortingState,
    getFilteredRowModel,
    getFacetedUniqueValues,
    PaginationState,
    ColumnDef
} from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Components
import { fuzzyFilter, multiSelectFilter } from './helpers';

import { TableTemplate } from './template';
import { ICommonTableProps } from './types';

interface TablePaginatedProps<T> extends ICommonTableProps<T> {
    columns: ColumnDef<T, any>[];
    queryKey: string;
    queryParams?: Record<string, any>;
    defaultSort?: string;
    queryCb: (args: any) => any;
    highlightRow?: (args: any) => boolean;
}

export function TablePaginated<T>(props: TablePaginatedProps<T>) {
    const { t } = useTranslation('components', { keyPrefix: 'tables.common' });
    const [columnVisibility, setColumnVisibility] = React.useState(props.visibleColumns ?? {});
    const [globalFilter, setGlobalFilter] = React.useState('');
    const [selectedColFilters, setSelectedColFilters] = React.useState<Record<string, string[]>>({});
    const [{ pageIndex, pageSize }, setPagination] = React.useState<PaginationState>({
        pageIndex: 1,
        pageSize: 10
    });
    const [sorting, setSorting] = React.useState<SortingState>(
        props.defaultSort ? [{ id: props.defaultSort, desc: false }] : []
    );
    const fetchDataOptions = React.useMemo(() => {
        return {
            pageData: {
                index: pageIndex,
                size: pageSize
            },
            sort: sorting[0],
            filter: selectedColFilters,
            search: globalFilter
        };
    }, [pageIndex, pageSize, sorting, globalFilter, selectedColFilters]);

    React.useEffect(() => {
        setColumnVisibility(props.visibleColumns ?? {});
    }, [props.visibleColumns, setColumnVisibility]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const defaultData = React.useMemo(() => [], []);
    const { data, isFetching, isLoading } = useQuery({
        queryKey: [props.queryKey, { ...fetchDataOptions, ...(props.queryParams ?? {}) }],
        queryFn: async () => {
            const data = await props.queryCb(fetchDataOptions);
            return data;
        },
        keepPreviousData: true,
        refetchOnWindowFocus: false
    });

    const table = useReactTable({
        columns: props.columns,
        data: data?.rows ?? defaultData,
        getCoreRowModel: getCoreRowModel(),
        debugTable: import.meta.env.NODE_ENV === 'development',
        debugHeaders: import.meta.env.NODE_ENV === 'development',
        debugColumns: import.meta.env.NODE_ENV === 'development',
        state: {
            // globalFilter,
            columnFilters,
            sorting,
            columnVisibility,
            pagination: { pageIndex, pageSize }
        },
        pageCount: data?.pageCount + 1 ?? -1,
        getFacetedUniqueValues: getFacetedUniqueValues(),
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onColumnVisibilityChange: setColumnVisibility,
        onSortingChange: setSorting,
        getFilteredRowModel: getFilteredRowModel(),
        // getSortedRowModel: getSortedRowModel(),
        enableColumnFilters: !!props.enableColumnFilters,
        manualPagination: true,
        onPaginationChange: setPagination,
        // globalFilterFn: fuzzyFilter,
        filterFns: {
            fuzzy: fuzzyFilter,
            multi: multiSelectFilter
        },
        defaultColumn: {
            minSize: 0,
            size: 0
        }
    });
    const state = table.getState();
    const activeFilters = React.useMemo(() => {
        const count = globalFilter?.length ? 1 : 0;
        const colFilterCount = (state.columnFilters ?? []).reduce((acc, curr) => {
            return acc + ((curr?.value as string[])?.length ?? 0);
        }, 0);

        return count + colFilterCount;
    }, [state]);

    React.useEffect(() => {
        const formattedColFilters = (state.columnFilters ?? []).reduce((acc, curr) => {
            return {
                ...acc,
                [curr.id]: curr.value
            };
        }, {});

        setSelectedColFilters(formattedColFilters);
    }, [activeFilters, columnFilters]);

    React.useEffect(() => {
        if (data?.pageCount > 0 && pageIndex > data?.pageCount) {
            table.setPageIndex(data?.pageCount);
        } else if (data?.pageCount === 0) {
            table.setPageIndex(0);
        } else if (data?.pageCount > 0 && pageIndex === 0) {
            table.setPageIndex(1);
        }
    }, [data?.pageCount]);

    return (
        <TableTemplate<T>
            {...{ table, globalFilter, setGlobalFilter, activeFilters, pageIndex }}
            loading={isLoading || isFetching}
            rightControl={props.rightControl}
            noMargin={props.noMargin}
            searchable={props.searchable}
            noHeader={props.noHeader}
            highlightRow={props.highlightRow}
            enableColumnFilters={props.enableColumnFilters}
            placeholder={props.placeholder}
            // searchDisabled={!data?.rows.length}
            showPagination={data?.pageCount > 1}
        />
    );
}
